/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Call Center license assignment.
 *
 * The organisation buys N seats from the platform; this is where an
 * administrator decides which N of their users get them. Modelled on the
 * existing "Usage Statistics" card in User Management so it reads as part of
 * the same screen rather than a bolted-on feature.
 *
 * Renders nothing when the add-on was never purchased — a tenant that has not
 * bought Call Center should not see controls for it.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  CircularProgress,
  Divider,
  LinearProgress,
  Snackbar,
  Switch,
  Tooltip,
} from "@mui/material";
import { HeadsetMic } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";

const MODULE = "CALL_CENTER";

export default function CallCenterSeats({ users }) {
  const { axiosInstance, user: currentUser } = useAuth();

  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingUserId, setSavingUserId] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/license/module-seats/${MODULE}`);
      setSeats(res.data);
    } catch {
      // Not entitled, or the module isn't installed for this tenant — either
      // way the section simply doesn't apply.
      setSeats(null);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const assigned = useMemo(
    () => new Set(seats?.assigned_user_ids || []),
    [seats]
  );

  const isSystemAdmin =
    String(currentUser?.role || "").toLowerCase() === "system_admin";

  const toggle = async (targetUser, next) => {
    setSavingUserId(targetUser.id);
    setError("");
    try {
      const action = next ? "assign" : "revoke";
      const res = await axiosInstance.post(
        `/license/module-seats/${MODULE}/${action}`,
        { user_id: targetUser.id }
      );
      setSeats(res.data.seats);
      setToast(
        next
          ? `${displayName(targetUser)} can now take calls.`
          : `${displayName(targetUser)}'s Call Center license was released.`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Could not change that license."
      );
      // The server is the authority on seat counts; re-read rather than guess.
      load();
    } finally {
      setSavingUserId(null);
    }
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" py={3}>
        <CircularProgress size={22} />
      </MDBox>
    );
  }

  // Never bought: show nothing rather than an empty control panel.
  if (!seats || !seats.purchased) return null;

  const assignable = (users || []).filter(
    (u) => u.is_active !== false && !u.is_support_user
  );
  const pct = seats.total_seats
    ? Math.min(100, (seats.used_seats / seats.total_seats) * 100)
    : 0;
  const full = seats.seats_remaining <= 0;

  return (
    <MDBox
      display="flex"
      flexDirection="column"
      p={2}
      border="1px solid #e0e0e0"
      borderRadius="lg"
      mt={3}
    >
      <MDBox display="flex" alignItems="center" gap={1} mb={1}>
        <HeadsetMic fontSize="small" sx={{ color: "#5C6B82" }} />
        <MDBox color="text" fontWeight="bold" fontSize="0.875rem">
          Call Center licenses
        </MDBox>
      </MDBox>

      <MDBox display="flex" justifyContent="space-between" mb={0.5}>
        <MDBox>Seats used:</MDBox>
        <MDBox fontWeight="bold">
          {seats.used_seats} of {seats.total_seats}
        </MDBox>
      </MDBox>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={full ? "warning" : "info"}
        sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
      />

      {full && (
        <Alert severity="info" sx={{ mb: 2, py: 0.25 }}>
          All Call Center licenses are assigned. Release one below, or contact
          your account manager to buy more seats.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2, py: 0.25 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Divider sx={{ my: 1 }} />

      <MDTypography variant="caption" color="text" mb={1}>
        Only licensed users can place or receive calls and be added to a queue.
      </MDTypography>

      <MDBox display="flex" flexDirection="column">
        {assignable.map((u) => {
          const has = assigned.has(u.id);
          // A seat can always be released; granting one needs capacity.
          const blocked = !has && full;
          return (
            <MDBox
              key={u.id}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              py={0.75}
              sx={{ borderBottom: "1px solid #F0F2F5" }}
            >
              <MDBox minWidth={0}>
                <MDTypography variant="button" color="dark" display="block" noWrap>
                  {displayName(u)}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {u.email || u.username}
                </MDTypography>
              </MDBox>
              <Tooltip
                title={
                  !isSystemAdmin
                    ? "Only a system administrator can change licenses"
                    : blocked
                    ? "No Call Center licenses are free"
                    : has
                    ? "Release this license"
                    : "Give this user a Call Center license"
                }
              >
                <span>
                  <Switch
                    checked={has}
                    disabled={!isSystemAdmin || blocked || savingUserId === u.id}
                    onChange={(e) => toggle(u, e.target.checked)}
                  />
                </span>
              </Tooltip>
            </MDBox>
          );
        })}
        {assignable.length === 0 && (
          <MDTypography variant="button" color="text">
            No active users to license.
          </MDTypography>
        )}
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}

function displayName(u) {
  const parts = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return parts || u.username || u.email || `User ${u.id}`;
}

CallCenterSeats.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object),
};

CallCenterSeats.defaultProps = { users: [] };
